import React from 'react';
import { Mic, AudioLines, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * AudioRecorder Component
 * Handles both Whisper transcription and realtime audio modes with integrated UI
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether recording is disabled
 * @param {Object} props.assistantColors - Color scheme for the UI
 * @param {Object} props.instance - Audio recorder instance from useAudioRecorder hook
 * @param {boolean} props.instance.isRecording - Whether currently recording
 * @param {boolean} props.instance.isTranscribing - Whether transcribing (Whisper mode only)
 * @param {Float32Array} props.instance.audioData - Audio frequency data for visualization
 * @param {Function} props.instance.startRecording - Start recording audio
 * @param {Function} props.instance.stopRecording - Stop recording audio
 * @param {Object} props.instance.realtimeConfig - Realtime connection configuration and state
 */
const AudioRecorder = ({
  disabled,
  assistantColors,
  instance: {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    realtimeConfig
  }
}) => {
  const { t } = useTranslation();

  // Determine current mode based on realtime connection
  const mode = realtimeConfig.isConnected ? 'realtime' : 'transcribe';

  const handleClick = (event) => {
    event.preventDefault();
    if (disabled || isTranscribing || (mode === 'realtime' && !realtimeConfig.isConnected)) return;

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const isDisabled = disabled || isTranscribing || (mode === 'realtime' && !realtimeConfig.isConnected);
  const primaryColor = assistantColors?.primaryColor || 'black';

  const label = isRecording
    ? (t('finishRecording') || "Finish recording")
    : (t('recordAudio') || "Record audio");

  const Icon = isRecording ? Check : mode === 'realtime' ? AudioLines : Mic;

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={isDisabled}
        onClick={handleClick}
        className="flex items-center justify-center w-[30px] h-[30px] rounded-lg border-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: isRecording ? primaryColor : 'transparent',
          color: isRecording ? 'white' : '#6b7280',
        }}
        onMouseEnter={(e) => {
          if (!e.currentTarget.disabled && !isRecording) {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
            e.currentTarget.style.color = '#111827';
          }
        }}
        onMouseLeave={(e) => {
          if (!isRecording) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#6b7280';
          }
        }}
      >
        <Icon size={16} />
      </button>
    </div>
  );
};

export default AudioRecorder;
